<template>
  <v-navigation-drawer
    :value="show"
    temporary
    fixed
    right
    :stateless="stateless"
    width="500"
    @input="$emit('update:show', $event)"
  >
    <DrawerToolbar
      :title="drawerTitle"
      admin
      :showBackButton="showGroupAssignmentPage"
      :showCloseButton="!showGroupAssignmentPage"
      @close="handleClose"
      @back="$emit('back-from-user-assignment')"
    />
    <v-card-text v-if="!showGroupAssignmentPage && show">
      <v-form ref="groupEditForm" v-model="formValid" class="form-rows">
        <v-text-field
          outlined
          class="pa-1"
          v-model="groupDisplayName"
          :disabled="readonly || groupData.groupType === 'Inform'"
          :rules="[
            (v) => !!v || 'Group name is required',
            (v) => !!(v && v.length < 255) || 'Group name should be shorter',
          ]"
          hide-details="auto"
          label="Group name*"
        ></v-text-field>
        <v-text-field
          outlined
          v-if="!['List', 'Complex', 'Inform'].includes(groupData.groupType)"
          class="pa-1"
          v-model="groupData.enrollmentRules"
          :readonly="true"
          label="Group Rules"
          hide-details="auto"
        ></v-text-field>
        <v-text-field
          outlined
          v-if="groupData.groupType == 'List' && groupData.hierarchyRules"
          class="pa-1"
          v-model="groupData.hierarchyRules"
          :readonly="readonly || !isWhistleClient"
          label="Special Rules"
        ></v-text-field>
        <div class="d-flex align-center justify-space-between mx-2">
          <Typography as="p" variant="body-1" class="text-left ma-0">
            Creator:
            <Typography as="span" variant="subtitle-1">
              {{ creatorName }}
            </Typography>
          </Typography>
          <Button
            v-if="canEditInformGroup && groupData.groupType === 'Inform' && !readonly"
            outlined
            iconLeft="mdi-pencil-outline"
            @click="$emit('edit-inform-group', groupData.groupId)"
            >Edit Group</Button
          >
        </div>
        <v-checkbox
          v-if="groupData.includeSubClients"
          disabled
          label="Includes sub-clients"
          :input-value="true"
          color="brandCyan"
          class="pa-1"
          dense
        />
        <v-checkbox
          disabled
          label="External users allowed"
          :input-value="
            !groupData.includeSubClients && !['Complex', 'Inform'].includes(groupData.groupType)
          "
          color="brandCyan"
          class="pa-3"
          dense
        />
        <p class="text--caption" v-if="groupData.rules != null && groupData.groupType !== 'Inform'">
          Note: Rules for a complex group cannot be edited at this time. Please make a new group.
        </p>
        <Alert
          v-if="groupData.groupType !== 'List'"
          variant="info"
          message="This is a dynamic group and its members cannot be individually edited."
          class="mx-1"
        />
        <v-btn
          text
          color="primary"
          class="mx-3 d-flex justify-start"
          v-if="showQrCodeButton"
          @click="$emit('load-qr-code', groupData.groupId)"
        >
          <v-icon left>mdi-qrcode</v-icon>View QR Code</v-btn
        >
        <v-btn
          v-if="!readonly && groupData.groupType === 'List'"
          text
          color="primary"
          class="mx-3 d-flex justify-start"
          @click="$emit('load-user-assignment')"
        >
          <v-icon left>mdi-account-group-outline</v-icon>Add people to group</v-btn
        >
        <div v-if="!readonly" class="d-flex align-center px-3">
          <Button
            text
            iconLeft="mdi-refresh"
            :loading="groupData.loadingGroupRefresh"
            class="mr-2"
            @click="handleRefresh"
            >Refresh group</Button
          >
          <Button
            text
            iconLeft="mdi-download"
            :disabled="groupData.loadingUsers || groupData.GroupAssignments.length === 0"
            @click="
              $emit('download-group', {
                groupId: groupData.groupId,
                clientId: groupData.clientId,
              })
            "
            >Download group</Button
          >
        </div>
        <!-- {{ groupData.groupAssignments.length }} -->
        <v-data-table
          :headers="tableHeaders"
          :items="groupData.GroupAssignments"
          item-key="userId"
          class="group-table"
          :loading="groupData.loadingUsers"
          loading-text="Loading... Please wait"
          :no-data-text="emptyGroupMessage"
          :items-per-page="groupData.userTable ? groupData.userTable.itemsPerPage : 10"
        >
          <template v-slot:[`item.delete`]="{ item }">
            <div v-if="!readonly && groupData.groupType === 'List'">
              <v-btn icon color="red" @click="$emit('delete-assignment', item.userId)">
                <v-icon>mdi-close-box-outline</v-icon>
              </v-btn>
            </div>
          </template>
          <template v-slot:[`item.name`]="{ item }">
            <span class="word-break">{{ item.User.firstName + " " + item.User.lastName }}</span>
          </template>
          <template v-slot:[`item.displayName`]="{ item }">
            <span class="word-break">{{ item.displayName }}</span>
          </template>
          <template v-slot:[`item.businessEmail`]="{ item }">
            {{ item.User ? item.User.businessEmail || item.User.businessPhone : "" }}
          </template>
        </v-data-table>
      </v-form>
    </v-card-text>

    <v-card-text class="mt-3 px-7 d-flex flex-column" v-else-if="show">
      <v-text-field
        v-model="userSearchValue"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
        outlined
        dense
        class="mt-6 mb-3"
        @input="$emit('update:userSearch', $event)"
      ></v-text-field>
      <UserTable
        ref="user-table"
        :selected="groupData.GroupAssignments"
        :search="userSearch"
        :displayEmail="true"
        @select-item="$emit('add-user-to-group', $event)"
      />
    </v-card-text>
    <v-card-actions v-if="!showGroupAssignmentPage" class="pb-4">
      <Button
        v-if="!readonly"
        color="red"
        text
        @click="$emit('delete-group')"
        iconLeft="mdi-delete"
      >
        Delete Group
      </Button>
      <Button
        v-if="!readonly && groupData.groupType !== 'Inform'"
        @click="handleUpdate"
        :disabled="!formValid"
        >Save</Button
      >
      <Button v-if="readonly" outlined @click="handleClose">Close</Button>
    </v-card-actions>
    <v-card-actions class="py-2" v-else> </v-card-actions>
  </v-navigation-drawer>
</template>

<script>
import Alert from "@/components/generic/Alert.vue";
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import Button from "@/components/generic/Button.vue";
import Typography from "@/components/generic/Typography.vue";
import UserTable from "@/components/data-table/Users.vue";

export default {
  name: "EditGroupDrawer",
  components: {
    Alert,
    DrawerToolbar,
    UserTable,
    Button,
    Typography,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    stateless: {
      type: Boolean,
      default: false,
    },
    groupData: {
      type: Object,
      default: () => ({}),
    },
    showGroupAssignmentPage: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    isWhistleClient: {
      type: Boolean,
      default: false,
    },
    showQrCodeButton: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    userSearch: {
      type: String,
      default: "",
    },
    canEditInformGroup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formValid: false,
      groupDisplayName: "",
      userSearchValue: "",
    };
  },
  computed: {
    drawerTitle() {
      return this.showGroupAssignmentPage
        ? "Add people"
        : this.readonly
        ? "View Group"
        : "Editing Group";
    },
    tableHeaders() {
      if (this.groupData && this.groupData.groupType !== "List") {
        return this.columns.slice(1);
      }
      return this.columns;
    },
    emptyGroupMessage() {
      if (this.groupData && this.groupData.groupType === "Inform") {
        return "No people in group. Please double check your query.";
      }
      return "No people in group";
    },
    creatorName() {
      return (this.groupData && this.groupData.creatorName) || "N/A";
    },
  },
  methods: {
    handleUpdate() {
      if (this.formValid) {
        this.$emit("update", {
          groupDisplayName: this.groupDisplayName,
        });
      }
    },
    handleClose() {
      this.$emit("close");
    },
    handleRefresh() {
      if (this.groupData.groupType === "Inform") {
        this.$emit("refresh-inform-group", this.groupData.groupId);
      } else {
        this.$emit("refresh-group", {
          groupId: this.groupData.groupId,
          clientId: this.groupData.clientId,
        });
      }
    },
  },
  watch: {
    show(val) {
      if (val && this.groupData) {
        this.groupDisplayName = this.groupData.groupDisplayName || "";
      }
    },
    "groupData.groupDisplayName": {
      handler(val) {
        if (val) {
          this.groupDisplayName = val;
        }
      },
      immediate: true,
    },
    userSearch: {
      handler(val) {
        this.userSearchValue = val;
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.word-break {
  word-break: break-word;
}
.hidden {
  visibility: hidden;
}
.form-rows {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
